// Reshape verbs — `pivot`, `fold`, `spread`, `unroll`. Together they
// cover the long↔wide and array-cell ↔ rows/cols reshape surface.
// Implementations are clean-room rewrites per the Phase 4b plan —
// they preserve the semantic intent of the prior tool's reshape
// ops but pick kwarg shapes that fit the s-expr substrate naturally.

import { z } from 'zod';

import { TypeMismatchError, UnknownOpError } from '../../../errors.ts';
import { astShape, opCallPos } from '../../../normalize.ts';
import {
  aggMeta,
  type ColumnRefInArg,
  createOpTable,
  type NameTable,
  type PropagateSchemaCtx,
  pipelineVerbs,
  type SpecialVerbCtx,
} from '../../../op-meta.ts';
import { paramPath } from '../../../path.ts';
import { compileAggSpec, materializedToRows } from '../../../runtime/arquero.ts';
import { type ColumnType, type Schema, schemaFromEntries, unknownCol } from '../../../schema/types.ts';
import { type Cell, MaterializedDataset } from '../../../value.ts';
import { aggContextScope } from '../agg-funcs.ts';

// biome-ignore lint/style/useNamingConvention: $-prefixed engine reserved
const ColSymbol = z.object({ $sym: z.string().min(1) });

// `:limit` cap for spread / unroll — guards against unbounded fan-out.
const DEFAULT_RESHAPE_LIMIT = 100;
const MAX_RESHAPE_LIMIT = 10000;

const LimitSchema = z.coerce.number().int().nonnegative().max(MAX_RESHAPE_LIMIT).optional();

// ## pivot
//
// (pivot key-col value-col) — long → wide. Distinct values of key-col
// become new column names; corresponding value-col values fill the
// cells. The remaining columns become the "id columns" that index
// the output rows. Collisions (same id × same key) collapse via
// `:agg` (default: pick first).

export const pivot = z
  .object({
    name: z.literal('pivot'),
    params: z.tuple([ColSymbol, ColSymbol]),
    agg: z
      .object({
        name: z.string().min(1),
        params: z.array(z.unknown()).optional(),
      })
      .optional(),
  })
  .describe(
    'Long → wide reshape. Positional: key-col, value-col. `:agg` is an optional agg-func call to collapse duplicates at (id × key).',
  )
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    childScopes: [aggContextScope],
    example: { expr: '(transform ds (pivot region revenue))' },
    apply: (data, dataset: MaterializedDataset, ctx: SpecialVerbCtx): MaterializedDataset => {
      const callPos = opCallPos(ctx.spans, ctx.path);
      const keyCol = data.params[0].$sym;
      const valueCol = data.params[1].$sym;
      if (!dataset.schema.has(keyCol)) {
        throw new TypeMismatchError(`pivot key-col \`${keyCol}\` to exist`, 'no such column', 'pivot', callPos);
      }
      if (!dataset.schema.has(valueCol)) {
        throw new TypeMismatchError(`pivot value-col \`${valueCol}\` to exist`, 'no such column', 'pivot', callPos);
      }
      const idCols = Array.from(dataset.schema.keys()).filter((c) => c !== keyCol && c !== valueCol);

      const aggReducer = compileOptionalAggCall(data.agg, paramPath(ctx.path, 0));
      const rows = materializedToRows(dataset);

      // Group rows by (idCols...), then collect per-key value(s).
      const groupMap = new Map<string, { idVals: Record<string, unknown>; perKey: Map<string, unknown[]> }>();
      const distinctKeys = new Set<string>();
      for (const row of rows) {
        const idKey = idCols.map((c) => JSON.stringify(row[c] ?? null)).join('\x00');
        const keyVal = String(row[keyCol]);
        distinctKeys.add(keyVal);
        let bucket = groupMap.get(idKey);
        if (bucket === undefined) {
          const idVals: Record<string, unknown> = {};
          for (const c of idCols) {
            idVals[c] = row[c];
          }
          bucket = { idVals, perKey: new Map() };
          groupMap.set(idKey, bucket);
        }
        const cellList = bucket.perKey.get(keyVal) ?? [];
        cellList.push(row[valueCol]);
        bucket.perKey.set(keyVal, cellList);
      }

      const sortedKeys = Array.from(distinctKeys).sort();
      const outRows: Record<string, Cell>[] = [];
      for (const { idVals, perKey } of groupMap.values()) {
        const out: Record<string, Cell> = { ...(idVals as Record<string, Cell>) };
        for (const k of sortedKeys) {
          const values = perKey.get(k) ?? [];
          out[k] = (aggReducer === null ? (values[0] ?? null) : aggReducer(values.map((v) => ({ value: v })))) as Cell;
        }
        outRows.push(out);
      }

      const outCols: Record<string, Cell[]> = {};
      for (const c of [...idCols, ...sortedKeys]) {
        outCols[c] = outRows.map((r) => (r[c] ?? null) as Cell);
      }
      const entries: [string, ColumnType][] = idCols.map((c) => [c, dataset.schema.get(c) ?? unknownCol]);
      for (const k of sortedKeys) {
        entries.push([k, unknownCol]);
      }
      return new MaterializedDataset(outCols, schemaFromEntries(entries));
    },
    // No `propagateSchema` — pivot's output columns depend on
    // RUNTIME values of `keyCol`; we can't statically know them.
    // The dispatch wrapper falls through to the apply-built schema
    // (dataset columns ↔ schema match guaranteed by the apply's
    // own `MaterializedDataset` construction).
    referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
      const parsed = data as z.infer<typeof pivot>;
      return [
        { name: parsed.params[0].$sym, path: paramPath(basePath, 0) },
        { name: parsed.params[1].$sym, path: paramPath(basePath, 1) },
      ];
    },
  });

// ## fold
//
// (fold [c1 c2 c3] :key-name k :value-name v) — wide → long. The
// listed columns are melted into two columns (`:key-name` and
// `:value-name`); each input row produces N output rows (one per
// listed column).

export const fold = z
  .object({
    name: z.literal('fold'),
    params: z.tuple([z.array(ColSymbol).min(1)]),
    'key-name': ColSymbol.optional(),
    'value-name': ColSymbol.optional(),
  })
  .describe(
    'Wide → long reshape. Positional: vector of columns to melt. `:key-name` / `:value-name` rename the output key/value columns.',
  )
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform ds (fold [q1 q2 q3] :key-name quarter :value-name revenue))' },
    apply: (data, dataset: MaterializedDataset, ctx: SpecialVerbCtx): MaterializedDataset => {
      const callPos = opCallPos(ctx.spans, ctx.path);
      const meltCols = data.params[0].map((s) => s.$sym);
      const keyName = data['key-name']?.$sym ?? 'key';
      const valueName = data['value-name']?.$sym ?? 'value';
      for (const c of meltCols) {
        if (!dataset.schema.has(c)) {
          throw new TypeMismatchError(`fold column \`${c}\` to exist`, 'no such column', 'fold', callPos);
        }
      }
      const idCols = Array.from(dataset.schema.keys()).filter((c) => !meltCols.includes(c));
      if (idCols.includes(keyName) || idCols.includes(valueName)) {
        throw new TypeMismatchError(
          'fresh key/value names',
          `:key-name \`${keyName}\` or :value-name \`${valueName}\` collides with an id column`,
          'fold',
          callPos,
        );
      }
      const rows = materializedToRows(dataset);
      const outRows: Record<string, Cell>[] = [];
      for (const row of rows) {
        for (const c of meltCols) {
          const out: Record<string, Cell> = {};
          for (const id of idCols) {
            out[id] = row[id] as Cell;
          }
          out[keyName] = c as Cell;
          out[valueName] = row[c] as Cell;
          outRows.push(out);
        }
      }
      const outCols: Record<string, Cell[]> = {};
      for (const c of [...idCols, keyName, valueName]) {
        outCols[c] = outRows.map((r) => (r[c] ?? null) as Cell);
      }
      return new MaterializedDataset(outCols, propagateFoldSchema(dataset.schema, meltCols, keyName, valueName));
    },
    propagateSchema: (data, ctx: PropagateSchemaCtx): Schema => {
      const parsed = data as z.infer<typeof fold>;
      const meltCols = parsed.params[0].map((s) => s.$sym);
      const keyName = parsed['key-name']?.$sym ?? 'key';
      const valueName = parsed['value-name']?.$sym ?? 'value';
      return propagateFoldSchema(ctx.inputSchema, meltCols, keyName, valueName);
    },
    referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
      const parsed = data as z.infer<typeof fold>;
      return parsed.params[0].map(
        (s, i) => ({ name: s.$sym, path: paramPath(paramPath(basePath, 0), i) }) as ColumnRefInArg,
      );
    },
  });

const propagateFoldSchema = (
  inputSchema: Schema,
  meltCols: readonly string[],
  keyName: string,
  valueName: string,
): Schema => {
  const idCols = Array.from(inputSchema.keys()).filter((c) => !meltCols.includes(c));
  const entries: [string, ColumnType][] = idCols.map((c) => [c, inputSchema.get(c) ?? unknownCol]);
  entries.push([keyName, unknownCol]);
  entries.push([valueName, unknownCol]);
  return schemaFromEntries(entries);
};

// ## spread
//
// (spread col :limit N) — array column → multiple columns. Each
// element of the array becomes a new column named `col-0`, `col-1`,
// … up to `:limit` (default DEFAULT_RESHAPE_LIMIT). Rows whose array
// has fewer elements get `null` in the trailing columns.

export const spread = z
  .object({
    name: z.literal('spread'),
    params: z.tuple([ColSymbol]),
    limit: LimitSchema,
  })
  .describe('Array column → multiple columns. Each element becomes `<col>-<i>`; `:limit` caps the fan-out.')
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform ds (spread tags :limit 5))' },
    apply: (data, dataset: MaterializedDataset, ctx: SpecialVerbCtx): MaterializedDataset => {
      const callPos = opCallPos(ctx.spans, ctx.path);
      const col = data.params[0].$sym;
      const limit = data.limit ?? DEFAULT_RESHAPE_LIMIT;
      if (!dataset.schema.has(col)) {
        throw new TypeMismatchError(`spread column \`${col}\` to exist`, 'no such column', 'spread', callPos);
      }
      const rows = materializedToRows(dataset);
      // Detect actual width — max(arrayLength, limit).
      let width = 0;
      for (const row of rows) {
        const v = row[col];
        if (Array.isArray(v)) {
          width = Math.max(width, Math.min(v.length, limit));
        }
      }
      const idCols = Array.from(dataset.schema.keys()).filter((c) => c !== col);
      const spreadCols = Array.from({ length: width }, (_, i) => `${col}-${i}`);
      const outCols: Record<string, Cell[]> = {};
      for (const c of idCols) {
        outCols[c] = rows.map((r) => r[c] as Cell);
      }
      for (const [i, sc] of spreadCols.entries()) {
        outCols[sc] = rows.map((r) => {
          const v = r[col];
          return Array.isArray(v) ? ((v[i] ?? null) as Cell) : null;
        });
      }
      const entries: [string, ColumnType][] = idCols.map((c) => [c, dataset.schema.get(c) ?? unknownCol]);
      for (const sc of spreadCols) {
        entries.push([sc, unknownCol]);
      }
      return new MaterializedDataset(outCols, schemaFromEntries(entries));
    },
    // No `propagateSchema` — spread's output width depends on
    // RUNTIME data; the dispatch wrapper falls through to the
    // apply-built schema. Same rationale as `pivot`.
    referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
      const parsed = data as z.infer<typeof spread>;
      return [{ name: parsed.params[0].$sym, path: paramPath(basePath, 0) }];
    },
  });

// ## unroll
//
// (unroll col :limit N :index-name idx) — array column → multiple
// rows. Each element of the array becomes a new row. `:index-name`
// optionally adds a 0-based index column.

export const unroll = z
  .object({
    name: z.literal('unroll'),
    params: z.tuple([ColSymbol]),
    limit: LimitSchema,
    'index-name': ColSymbol.optional(),
  })
  .describe('Array column → multiple rows. `:limit` caps elements per row; `:index-name` adds an index column.')
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform ds (unroll tags :index-name pos))' },
    apply: (data, dataset: MaterializedDataset, ctx: SpecialVerbCtx): MaterializedDataset => {
      const callPos = opCallPos(ctx.spans, ctx.path);
      const col = data.params[0].$sym;
      const limit = data.limit ?? DEFAULT_RESHAPE_LIMIT;
      const indexName = data['index-name']?.$sym;
      if (!dataset.schema.has(col)) {
        throw new TypeMismatchError(`unroll column \`${col}\` to exist`, 'no such column', 'unroll', callPos);
      }
      const rows = materializedToRows(dataset);
      const otherCols = Array.from(dataset.schema.keys()).filter((c) => c !== col);
      const outRows: Record<string, Cell>[] = [];
      for (const row of rows) {
        const v = row[col];
        if (!Array.isArray(v)) {
          continue;
        }
        const truncated = v.slice(0, limit);
        for (const [i, el] of truncated.entries()) {
          const out: Record<string, Cell> = {};
          for (const c of otherCols) {
            out[c] = row[c] as Cell;
          }
          out[col] = el as Cell;
          if (indexName !== undefined) {
            out[indexName] = i as Cell;
          }
          outRows.push(out);
        }
      }
      const finalCols = indexName === undefined ? [...otherCols, col] : [...otherCols, col, indexName];
      const outCols: Record<string, Cell[]> = {};
      for (const c of finalCols) {
        outCols[c] = outRows.map((r) => (r[c] ?? null) as Cell);
      }
      const entries: [string, ColumnType][] = finalCols.map((c) => {
        if (c === col) {
          return [c, unknownCol]; // element type unknown
        }
        if (c === indexName) {
          return [c, unknownCol];
        }
        return [c, dataset.schema.get(c) ?? unknownCol];
      });
      return new MaterializedDataset(outCols, schemaFromEntries(entries));
    },
    propagateSchema: (data, ctx: PropagateSchemaCtx): Schema => {
      const parsed = data as z.infer<typeof unroll>;
      const col = parsed.params[0].$sym;
      const indexName = parsed['index-name']?.$sym;
      const otherCols = Array.from(ctx.inputSchema.keys()).filter((c) => c !== col);
      const entries: [string, ColumnType][] = otherCols.map((c) => [c, ctx.inputSchema.get(c) ?? unknownCol]);
      entries.push([col, unknownCol]);
      if (indexName !== undefined) {
        entries.push([indexName, unknownCol]);
      }
      return schemaFromEntries(entries);
    },
    referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
      const parsed = data as z.infer<typeof unroll>;
      return [{ name: parsed.params[0].$sym, path: paramPath(basePath, 0) }];
    },
  });

// ## shared helpers

/** Compile an optional agg-call to a reducer (for pivot's `:agg`). */
const compileOptionalAggCall = (
  aggCall: { readonly name: string; readonly params?: readonly unknown[] } | undefined,
  basePath: string,
): ((rows: readonly Record<string, unknown>[]) => unknown) | null => {
  if (aggCall === undefined) {
    return null;
  }
  const table: NameTable = createOpTable(aggContextScope.schemas as readonly z.ZodObject[]);
  const schema = table.get(aggCall.name);
  if (schema === undefined) {
    throw new UnknownOpError(aggCall.name, Array.from(table.keys()), opCallPos(new Map(), basePath));
  }
  const meta = aggMeta.get(schema);
  if (meta === undefined) {
    throw new Error(`pivot: schema for \`${aggCall.name}\` not registered in aggMeta`);
  }
  const spec = meta.compileAggExpr({ name: aggCall.name, params: aggCall.params ?? [] }, basePath);
  return compileAggSpec(spec);
};

// Reference astShape so the import isn't dead-code (preserves clarity
// of intent even when not used today; future verbs may reach for it).
void astShape;
